import React from 'react';
import gql from 'graphql-tag';

import {
  FilterProps,
  TableCellV8,
  SelectColumnV8,
  CellDateTimeV8,
  FormFilterCard,
  FieldDateRanger,
  FormCol,
  FormCol3,
  buildWhereForDates,
  CellFloatCustomV8,
  FormCol4,
} from 'motion-components';
import { Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n';
import { FieldSelectWasteReceptionPoint, FormFieldMultiMaterialDlg } from 'shared-pages/components';
import { promiseModalMultiSelect } from 'motion-components';

// -------------------------------------------------------------------------------
// Modal Multi-Select Έντυπα Αναγνώρισης
// -------------------------------------------------------------------------------

const gqlGet = gql`
  query getIdocs($limit: Int, $offset: Int, $order: String, $where: String) {
    getIdocs(limit: $limit, offset: $offset, order: $order, where: $where) {
      data {
        id
        identificationDocument
        dateCollected
        dateStorage
        collectionPointName
        colpointAddressLine1
        wastereceptionpointId
        wastereceptionpointCompanyName
        materialId
        material {
          id
          name
        }
        receptionNetWeight
        collectorCompanyName
        vehPlateNumber
        driverFullName
      }
      recordCount
    }
  }
`;

/** Μετατροπή comma-separated material ids σε $in για το where */
const parseMaterialIds = (materialId: string | undefined) => {
  if (!materialId) return undefined;
  const ids = materialId
    .split(',')
    .map((id) => parseInt(id.trim(), 10))
    .filter((id) => !Number.isNaN(id));
  return ids.length > 0 ? ids : undefined;
};

// -------------------------------------------------------------------------------
// Filters
// -------------------------------------------------------------------------------
const buildWhereFromFilters = (values) => {
  let where: Record<string, unknown> = {
    isCancelled: false,
    // Μόνο έντυπα που δεν έχουν ήδη συνδεθεί με εσωτερική διακίνηση
    transferredWithITransId: { $eq: null },
  };

  if (values) {
    if (values.startDate || values.endDate) {
      where = {
        ...where,
        ...buildWhereForDates(values.startDate, values.endDate, 'dateStorage'),
      };
    }
    if (values.wastereceptionpointId) {
      where = { ...where, wastereceptionpointId: values.wastereceptionpointId };
    }
    const materialIds = parseMaterialIds(values.materialId);
    if (materialIds) {
      where = { ...where, materialId: { $in: materialIds } };
    }
    if (values.isActive !== undefined && values.isActive !== null && values.isActive !== '') {
      where = { ...where, isActive: values.isActive };
    }
  }
  return where;
};

const Filters: (props: FilterProps) => JSX.Element = ({ onChange, initialValues }) => {
  const { t } = useTranslation();

  const filters = {
    startDate: initialValues?.startDate ?? null,
    endDate: initialValues?.endDate ?? null,
    wastereceptionpointId: initialValues?.wastereceptionpointId ?? undefined,
    materialId: initialValues?.materialId ?? '',
    materialName: initialValues?.materialName ?? '',
    isActive: initialValues?.isActive ?? true,
  };

  return (
    <FormFilterCard title={t('Κριτήρια Αναζήτησης')} onSubmit={onChange} initialValues={filters}>
      <Row>
        <FieldDateRanger
          as={FormCol4}
          label={t('Παράδοση από')}
          label2={t('Έως')}
          name="startDate"
          name2="endDate"
        />
        <FieldSelectWasteReceptionPoint
          as={FormCol4}
          label={t('Σημείο Παραλαβής Αποβλήτων')}
          name="wastereceptionpointId"
        />
        <FormFieldMultiMaterialDlg
          as={FormCol4}
          label={t('Απόβλητα')}
          name="materialId"
          displayName="materialName"
          initValueId={filters.materialId}
          initValueName={filters.materialName}
          initFilters={{ isActive: true }}
          onValueChange={() => {}}
        />
      </Row>
    </FormFilterCard>
  );
};

// -------------------------------------------------------------------------------
const s = typeof SelectColumnV8 === 'function' ? SelectColumnV8() : SelectColumnV8;
const columns: Array<any> = [
  s,
  {
    id: 'identificationDocument',
    header: 'Κωδικός Εντύπου',
    columnName: 'Κωδικός Εντύπου',
    accessorKey: 'identificationDocument',
    orderByField: 'identificationDocument',
    cell: TableCellV8,
    size: 15,
  },
  {
    id: 'dateStorage',
    header: i18n.t('Ημερομηνία Παράδοσης'),
    columnName: i18n.t('Ημερομηνία Παράδοσης'),
    accessorKey: 'dateStorage',
    orderByField: 'dateStorage',
    cell: CellDateTimeV8,
    size: 20,
    meta: { type: 'text' },
  },
  {
    id: 'material.name',
    header: i18n.t('Απόβλητο'),
    columnName: i18n.t('Απόβλητο'),
    accessorKey: 'material.name',
    orderByField: 'material.name',
    cell: TableCellV8,
    size: 20,
    meta: { type: 'text' },
  },
  {
    id: 'collectionPointName',
    header: i18n.t('Προέλευση Αποβλήτων'),
    columnName: i18n.t('Προέλευση Αποβλήτων'),
    accessorKey: 'collectionPointName',
    orderByField: 'collectionPointName',
    cell: ({ row }) => (
      <div className="text-start">
        <div>{row.original.collectionPointName}</div>
        <div style={{ color: 'gray', fontSize: 'smaller' }}>
          {row.original.colpointAddressLine1}
        </div>
      </div>
    ),
    size: 25,
    meta: { type: 'text' },
  },
  {
    id: 'wastereceptionpointCompanyName',
    header: i18n.t('Παραλαβή Αποβλήτων'),
    columnName: i18n.t('Παραλαβή Αποβλήτων'),
    accessorKey: 'wastereceptionpointCompanyName',
    orderByField: 'wastereceptionpointCompanyName',
    cell: TableCellV8,
    size: 25,
    meta: { type: 'text' },
  },
  {
    id: 'receptionNetWeight',
    header: i18n.t('Βάρος Αποβλήτων'),
    columnName: i18n.t('Βάρος Αποβλήτων'),
    accessorKey: 'receptionNetWeight',
    orderByField: 'receptionNetWeight',
    cell: CellFloatCustomV8(8, 1),
    size: 15,
    meta: { type: 'text' },
  },
];

// -------------------------------------------------------------------------------
export const promiseModalSelectMultiIdocs = (props) => {
  return promiseModalMultiSelect({
    ...props,
    title: i18n.t('Επιλογή Εντύπων Αναγνώρισης'),
    gqlQuery: gqlGet,
    columns: columns,
    buildWhereFromFilters: buildWhereFromFilters,
    initFilters: props.initFilters,
    Filters: Filters,
    getRowId: (row) => row.id,
    idFieldName: 'id',
    nameFieldName: 'identificationDocument',
    orderBy: 'dateStorage DESC',
    autoFetchData: true,
    size: 'xl',
  });
};
// -------------------------------------------------------------------------------
